import { FileText, Package } from "lucide-react";
import { type SyntheticEvent, useState } from "react";
import { RecordWindow } from "../../web/components/RecordWindow";
import { ErrorNotice } from "../../web/components/ui";
import { usePriceListSaver } from "./data";
import { ITEM_NUMBER_MAX } from "./meta";

/** Adding a material: a name (and item number), then it opens in its own window to fill in the rest. */
export function NewItemWindow(props: Readonly<{ onCreated: (uuid: string) => void; onClose: () => void }>) {
    const saver = usePriceListSaver();
    const [name, setName] = useState("");
    const [itemNumber, setItemNumber] = useState("");
    const [synced, setSynced] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);

    const submit = (e: SyntheticEvent) => {
        e.preventDefault();
        if (!name.trim() || !itemNumber.trim()) return;
        setBusy(true);
        setError(null);
        saver
            .createMaterial({ name: name.trim(), item_number: itemNumber.trim(), synced })
            .then((m) => props.onCreated(m.uuid))
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };

    return (
        <RecordWindow kind="item" icon={<Package className="size-4" />}
            title="New item"
            tabs={[{ id: "details", label: "Details", icon: <FileText className="size-5" /> }]}
            tab="details"
            onTab={() => undefined}
            onClose={props.onClose}
        >
            <form className="mx-auto w-full max-w-lg space-y-4" onSubmit={submit}>
                <label className="block space-y-1">
                    <span className="text-xs font-medium text-muted">Name</span>
                    <input className="input text-sm" value={name} onChange={(e) => setName(e.target.value)} required />
                </label>
                <label className="block space-y-1">
                    <span className="text-xs font-medium text-muted">Item number</span>
                    <input className="input text-sm" value={itemNumber} maxLength={ITEM_NUMBER_MAX} onChange={(e) => setItemNumber(e.target.value)} required />
                    {/* Required: accounting software matches invoice lines to its items by it (rules.ts assertItemNumberGiven). */}
                    <span className="block text-xs text-muted">Unique to this item. Accounting software matches invoice lines to items by it.</span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-0.5 size-4" checked={synced} onChange={(e) => setSynced(e.target.checked)} />
                    <span>
                        <span className="block">Add to ServiceM8</span>
                        <span className="block text-xs text-muted">{synced ? "Synced with ServiceM8, so it can go on jobs." : "Kept only in this app. You can add it to ServiceM8 later."}</span>
                    </span>
                </label>
                <ErrorNotice error={error} />
                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-secondary" onClick={props.onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={busy || !name.trim() || !itemNumber.trim()}>
                        Add item
                    </button>
                </div>
                <p className="text-xs text-muted">Set its price, cost and the rest next.</p>
            </form>
        </RecordWindow>
    );
}
